import React from 'react';
import { Alert, Button, Checkbox, InputNumber, Radio, Typography } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import QuestionCheck from './QuestionCheck';
import ScreeningField from './ScreeningField';
import { getBmiNumber } from '../utils/screening';
import { bmi27ComorbidityOptions, bmi30ComorbidityOptions, yesNoUnknownOptions } from '../utils/constants';

const { Text } = Typography;

export default function BridgeScreeningPanel({ values, onChange, onSubmit, onReset, loading }) {
  const bmi = getBmiNumber(values);
  const comorbidityOptions =
    bmi !== null && bmi >= 30 && bmi < 35
      ? bmi30ComorbidityOptions
      : bmi !== null && bmi >= 27 && bmi < 30
        ? bmi27ComorbidityOptions
        : [];
  const update = (field, value) => {
    if (field === 'bmi_at_initiation') {
      onChange({ ...values, bmi_at_initiation: value, comorbidities: [] });
      return;
    }
    onChange({ ...values, [field]: value });
  };

  return (
    <section className="glp1-screener-panel" aria-labelledby="glp1-screener-title">
      <div className="glp1-screener-header">
        <div>
          <h2 id="glp1-screener-title" className="glp1-screener-title">
            Eligibility Screener
          </h2>
          <p className="glp1-screener-copy">
            Four de-identified checks from the CMS prescriber materials. Results become the latest Ask-mode context.
          </p>
        </div>
      </div>

      <div className="glp1-screener-steps">
        <QuestionCheck
          number={1}
          title="Part D coverage"
          question="Does the patient have eligible Medicare Part D plan coverage?"
          detail="Most people with Medicare Part D coverage are eligible. Certain plan types are excluded unless the person also has a standalone PDP."
          value={values.eligible_part_d_coverage}
          onChange={(value) => update('eligible_part_d_coverage', value)}
          loading={loading}
        />

        <QuestionCheck
          number={2}
          title="Prior Part D GLP-1"
          question="Has the patient received a GLP-1 through their Part D plan?"
          detail="If yes, CMS says the request should go to the patient's Part D plan instead of Bridge."
          value={values.received_glp1_through_part_d}
          onChange={(value) => update('received_glp1_through_part_d', value)}
          loading={loading}
        />

        <QuestionCheck
          number={3}
          title="Part D-coverable diagnosis"
          question="Does the patient have type 2 diabetes, moderate-to-severe sleep apnea, or MASH?"
          detail="If yes, CMS says the patient is not eligible for Bridge even if the BMI criteria are met."
          value={values.has_part_d_diagnosis}
          onChange={(value) => update('has_part_d_diagnosis', value)}
          loading={loading}
        />

        <div className="glp1-step">
          <div className="glp1-step-header">
            <span className="glp1-step-number">4</span>
            <div>
              <Text className="glp1-step-title">Clinical criteria</Text>
              <Text className="glp1-step-detail">
                Confirm weight-management-only use, BMI at therapy initiation, and the relevant comorbidity pathway.
              </Text>
            </div>
          </div>

          <div className="glp1-bmi-grid">
            <ScreeningField label="Is the GLP-1 only for reducing excess body weight and maintaining weight reduction?">
              <div className="glp1-step-controls">
                <Radio.Group
                  value={values.weight_management_only}
                  onChange={(event) => update('weight_management_only', event.target.value)}
                  disabled={loading}
                  options={yesNoUnknownOptions}
                  optionType="button"
                  buttonStyle="solid"
                />
              </div>
            </ScreeningField>

            <ScreeningField label="BMI at GLP-1 therapy initiation" help="Do not use current BMI if therapy started earlier.">
              <div className="glp1-step-controls">
                <InputNumber
                  value={values.bmi_at_initiation}
                  min={0}
                  max={100}
                  precision={1}
                  style={{ width: 180, maxWidth: '100%' }}
                  placeholder="Enter BMI"
                  onChange={(value) => update('bmi_at_initiation', value)}
                  disabled={loading}
                />
              </div>
            </ScreeningField>

            {bmi === null ? (
              <Alert type="info" showIcon message="Enter BMI to show the relevant clinical criteria." />
            ) : bmi >= 35 ? (
              <Alert type="success" showIcon message="BMI is at least 35. No listed comorbidity is required for this BMI pathway." />
            ) : bmi >= 27 ? (
              <ScreeningField label={bmi >= 30 ? 'Select any qualifying diagnosis for BMI 30-34.9' : 'Select any qualifying diagnosis for BMI 27-29.9'}>
                <Checkbox.Group
                  className="glp1-comorbidity-grid"
                  value={values.comorbidities}
                  options={comorbidityOptions}
                  onChange={(selectedValues) => {
                    let nextValues = selectedValues;
                    const exclusiveValues = selectedValues.filter((value) => value === 'none' || value === 'unknown');
                    if (exclusiveValues.length) {
                      nextValues = [exclusiveValues[exclusiveValues.length - 1]];
                    } else {
                      nextValues = selectedValues.filter((value) => value !== 'none' && value !== 'unknown');
                    }
                    update('comorbidities', nextValues);
                  }}
                  disabled={loading}
                />
              </ScreeningField>
            ) : (
              <Alert type="warning" showIcon message="BMI is below 27, which is below the lowest CMS Bridge BMI pathway." />
            )}
          </div>
        </div>
      </div>

      <div className="glp1-screener-actions">
        <Button type="primary" icon={<CheckCircleOutlined />} loading={loading} onClick={onSubmit}>
          Run screening
        </Button>
        <Button disabled={loading} onClick={onReset}>
          Reset
        </Button>
      </div>
    </section>
  );
}
